/**
 * career-llm-review — the human-in-the-loop review pane for LLM calls (§9.2 / D6).
 *
 * Mounted ONCE in the shell rather than on a route, because the pause happens wherever the operator
 * already is: they click something, that turn's LLM call parks in front of the provider, and this
 * is where they read it, rewrite it, and let it go. It renders NOTHING — no DOM at all — unless a
 * call of theirs is actually waiting.
 *
 * ## Off by default, and it costs nothing to be off
 *
 * The gate is the api's, not this element's: an ordinary caller's request is never gated, so this
 * pane could poll forever and still show nothing. It doesn't poll anyway. `attach()` returns
 * immediately for a session without the LLM-configuration capability (the same client-side hint the
 * settings surface uses — the server is the boundary), and even a capable session polls only after
 * `GET /api/llm/reviews` reports `enabled: true` for them. Switching the toggle off stops the poll
 * on the next tick.
 *
 * ## Why a bottom sheet and not an inline block
 *
 * §0 ("seamless document over cards") reserves real containment for "a momentary state chrome", and
 * this is exactly that: the operator's action is BLOCKED until they answer, so the answer must be
 * reachable without hunting for it — an inline block at the top of a scrolled surface would be
 * off-screen at the moment it matters. Inside the sheet the guidance holds: a heading, hairline
 * rules and monospace prompt blocks, no nested boxes. The prompt/params vocabulary is deliberately
 * the same one `/runs` already uses for a captured call, so reading a call before it runs looks
 * like reading one after.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, type TemplateResult } from "@platform/web-kit/render"
import { I18nToken, SessionToken, apiGet, apiPost } from "../services"
import { sessionCanConfigureLlm, type ShellSession } from "../domain"
import type { CareerI18n } from "../../i18n"

// ─── Wire view types (parsed from the untrusted /api/llm/reviews record) ────────

type ReasoningEffort = "low" | "medium" | "high"

interface ReviewParamsView {
  temperature?: number
  maxTokens?: number
  reasoningEffort?: ReasoningEffort
}

interface PendingReviewView {
  id: string
  action?: string
  seam?: string
  engine: string
  model: string
  system: string
  user: string
  params: ReviewParamsView
  responseSchema?: unknown
  openedAt: string
  expiresAt: string
}

const str = (v: unknown): string | undefined => (typeof v === "string" ? v : undefined)
const num = (v: unknown): number | undefined => (typeof v === "number" ? v : undefined)

function parseParams(raw: unknown): ReviewParamsView {
  if (!raw || typeof raw !== "object") return {}
  const p = raw as Record<string, unknown>
  const re = p["reasoningEffort"]
  return {
    ...(num(p["temperature"]) !== undefined ? { temperature: num(p["temperature"]) } : {}),
    ...(num(p["maxTokens"]) !== undefined ? { maxTokens: num(p["maxTokens"]) } : {}),
    ...(re === "low" || re === "medium" || re === "high" ? { reasoningEffort: re } : {}),
  }
}

/** Parse the pending list defensively — a malformed entry is dropped, never rendered half-read. */
export function parseReviews(raw: unknown): { enabled: boolean; pending: PendingReviewView[] } {
  if (!raw || typeof raw !== "object") return { enabled: false, pending: [] }
  const r = raw as Record<string, unknown>
  const pending = Array.isArray(r["pending"])
    ? (r["pending"] as unknown[]).flatMap((p): PendingReviewView[] => {
        const it = (p ?? {}) as Record<string, unknown>
        const id = str(it["id"])
        if (!id) return []
        return [
          {
            id,
            ...(str(it["action"]) ? { action: str(it["action"]) } : {}),
            ...(str(it["seam"]) ? { seam: str(it["seam"]) } : {}),
            engine: str(it["engine"]) ?? "",
            model: str(it["model"]) ?? "",
            system: str(it["system"]) ?? "",
            user: str(it["user"]) ?? "",
            params: parseParams(it["params"]),
            ...(it["responseSchema"] !== undefined ? { responseSchema: it["responseSchema"] } : {}),
            openedAt: str(it["openedAt"]) ?? "",
            expiresAt: str(it["expiresAt"]) ?? "",
          },
        ]
      })
    : []
  return { enabled: r["enabled"] === true, pending }
}

/** How long the operator still has, in whole seconds (never negative). */
export function secondsLeft(expiresAt: string, now: number): number {
  const ms = Date.parse(expiresAt) - now
  return Number.isFinite(ms) ? Math.max(0, Math.round(ms / 1000)) : 0
}

/** Poll cadence while the gate is on. Only a caller who opted in ever runs this. */
const POLL_MS = 1000

/** The width below which the sheet's own ceiling is tighter than the fields' natural size. */
const NARROW_PX = 560

/** The viewport width, or a desktop default where there is no window (tests, SSR). */
export function viewportWidth(): number {
  return typeof globalThis.innerWidth === "number" && globalThis.innerWidth > 0 ? globalThis.innerWidth : 1024
}

/**
 * How many ROWS a prompt field gets. Rows, not pixels: a row is a text line by definition of the
 * attribute, so the browser's own layout decides where the box ends and it cannot end mid-glyph.
 * (An `em`-based max-height looked equivalent and drifted from the computed line advance by half a
 * line — which was the defect.)
 *
 * It follows the content between a floor and a ceiling, and the ceiling is tighter on a phone,
 * where the whole sheet has less room. Longer text scrolls inside the field.
 */
export function promptRows(field: "system" | "user", value: string, width: number): number {
  const narrow = width <= NARROW_PX
  const max = field === "system" ? (narrow ? 4 : 6) : narrow ? 7 : 12
  return Math.max(2, Math.min(max, value.split("\n").length))
}

/** One allowlisted model the select offers (`GET /api/runs/models` — the D3/D4 registry). */
interface ModelOptionView {
  id: string
  label: string
}

/**
 * The select value that reveals the free-text field. A model NOT on the allowlist is a real need,
 * not an oversight: a subscription engine has its own namespace (claude answers to `sonnet`, and
 * the openrouter ids mean nothing to it), so a gate that could only offer the openrouter allowlist
 * could not fix the very mismatch it exposes. It is an explicit choice with an explicit warning,
 * never an unvalidated free-text box that looks like a safe one.
 */
export const MODEL_OTHER = "__other__"

/** Is this model id on the server's allowlist? Drives the honest warning under the free-text field. */
export function isAllowlisted(id: string, models: readonly ModelOptionView[]): boolean {
  return models.some((m) => m.id === id)
}

// ─── ViewModel ──────────────────────────────────────────────────────────────────

export class LlmReviewVm implements ViewModel {
  readonly pending: Signal<PendingReviewView[]> = signal<PendingReviewView[]>([])
  readonly enabled: Signal<boolean> = signal(false)
  readonly busy: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  /** Re-rendered every tick so the remaining-time line counts down honestly. */
  readonly tick: Signal<number> = signal(Date.now())
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]
  /** The allowlisted models the select offers — fetched once, the first time a call is parked. */
  readonly models: Signal<ModelOptionView[]> = signal<ModelOptionView[]>([])
  /** The chosen select value, or `null` to follow the paused call's own model. Reset per review. */
  readonly modelChoice: Signal<string | null> = signal<string | null>(null)
  /** Client-side capability hint — the api is the boundary; this only avoids pointless polling. */
  readonly capable: boolean
  private timer: ReturnType<typeof setInterval> | null = null
  /** The review the current `modelChoice` belongs to — a new call must not inherit it. */
  private choiceFor: string | null = null

  constructor(i18n: CareerI18n, session: ShellSession) {
    this.t = i18n.t
    this.capable = sessionCanConfigureLlm(session)
    this.renderDeps = [this.pending, this.enabled, this.busy, this.error, this.tick, this.models, this.modelChoice]
  }

  attach(): () => void {
    // An ordinary session never polls, never fetches, and never renders anything.
    if (!this.capable) return () => {}
    void this.poll()
    this.timer = setInterval(() => void this.poll(), POLL_MS)
    // A background poll must never be a reason for a process (or a test run) to stay alive.
    this.timer.unref?.()
    return () => {
      if (this.timer !== null) clearInterval(this.timer)
      this.timer = null
    }
  }

  /** One poll. A failure is shown, never thrown — a broken inspector must not break the app. */
  async poll(): Promise<void> {
    try {
      const { enabled, pending } = parseReviews(await apiGet<unknown>("/api/llm/reviews"))
      this.enabled.set(enabled)
      this.pending.set(pending)
      this.error.set(null)
      if (pending.length > 0) void this.loadModels()
      // A decision belongs to ONE call. When the parked call changes, the model choice resets to
      // that call's own model rather than silently carrying the previous review's selection over.
      const head = pending[0]?.id ?? null
      if (head !== this.choiceFor) {
        this.choiceFor = head
        this.modelChoice.set(null)
      }
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.tick.set(Date.now())
    }
  }

  /**
   * Load the model allowlist once — the SAME registry a rerun may target (`GET /runs/models`), not
   * a second list that could drift from it. Fetched lazily so a session with nothing parked never
   * asks; a failure degrades to the free-text field rather than blocking the decision.
   */
  async loadModels(): Promise<void> {
    if (this.models.value.length > 0) return
    try {
      const raw = await apiGet<unknown>("/api/runs/models")
      const list = (raw as { models?: unknown })?.models
      if (!Array.isArray(list)) return
      this.models.set(
        list.flatMap((m): ModelOptionView[] => {
          const it = (m ?? {}) as Record<string, unknown>
          const id = str(it["id"])
          return id ? [{ id, label: str(it["label"]) ?? id }] : []
        }),
      )
    } catch {
      // No allowlist ⇒ the field falls back to free text. Honest, and the decision still works.
    }
  }

  /** The select's current value: the operator's choice, else the paused call's own model. */
  selectedModel(review: PendingReviewView): string {
    return this.modelChoice.value ?? review.model
  }

  /** The call to answer: the one that has been waiting longest (the server sends them in order). */
  current(): PendingReviewView | undefined {
    return this.pending.value[0]
  }

  remaining(review: PendingReviewView): number {
    return secondsLeft(review.expiresAt, this.tick.value)
  }

  /**
   * Send the decision. The paused request resumes on the SERVER, so there is nothing to await here
   * beyond the acknowledgement — the pane simply stops showing a call that is no longer parked.
   */
  async resolve(id: string, body: Record<string, unknown>): Promise<void> {
    this.busy.set(true)
    try {
      await apiPost("/api/llm/reviews/resolve", { id, ...body })
      // Drop it locally at once so the pane cannot flash the answered call back for a poll cycle.
      this.pending.set(this.pending.value.filter((p) => p.id !== id))
      this.error.set(null)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.busy.set(false)
    }
  }
}

// ─── Element ────────────────────────────────────────────────────────────────────

export class CareerLlmReview extends KitElement<LlmReviewVm> {
  protected createViewModel(ctx: KitContext): LlmReviewVm {
    return new LlmReviewVm(ctx.inject(I18nToken), ctx.inject(SessionToken))
  }

  /**
   * One editable prompt message — the same `<pre>`-ish vocabulary /runs uses, made writable.
   *
   * The field FOLLOWS ITS CONTENT, in `rows` — see {@link promptRows}. A box whose last row is a
   * horizontal slice through the glyphs reads as broken no matter what it does, and a fixed
   * nine-row field for a two-line message is dead space that pushes the answer off screen.
   */
  private messageField(vm: LlmReviewVm, field: "system" | "user", value: string): TemplateResult {
    const rows = promptRows(field, value, viewportWidth())
    return html`
      <label class=${`hil-field hil-field--msg hil-field--${field}`}>
        <span class="hil-field-label" lang="de">${vm.t(field === "system" ? "hil.system" : "hil.user")}</span>
        <textarea class="hil-msg" data-field=${field} rows=${rows} .value=${value}></textarea>
      </label>
    `
  }

  /**
   * The model field — a SELECTION from the server's allowlist (the same registry a rerun targets),
   * not a free-text box. A typo'd id used to fail at the provider AFTER the operator had already
   * approved, which is the worst moment to learn it.
   *
   * The escape hatch stays, because it is load-bearing: a subscription engine answers to its own
   * names, so "anything not on the list" must remain reachable. It is now an explicit choice that
   * says what it means, and an id outside the allowlist is called out before the call runs.
   */
  private modelField(vm: LlmReviewVm, review: PendingReviewView): TemplateResult {
    const models = vm.models.value
    const selected = vm.selectedModel(review)
    // No allowlist reached us (the fetch failed) — degrade to free text rather than to no field.
    if (models.length === 0) {
      return html`
        <label class="hil-field hil-field--model">
          <span class="hil-field-label" lang="de">${vm.t("hil.model")}</span>
          <input class="hil-model" data-field="model" type="text" .value=${selected} spellcheck="false" />
        </label>
      `
    }
    const isOther = selected === MODEL_OTHER
    // The paused call's own model may predate the allowlist (or belong to a subscription engine);
    // it is offered as itself, flagged, rather than silently swapped for something else.
    const unlisted = !isOther && !isAllowlisted(review.model, models)
    return html`
      <label class="hil-field hil-field--model">
        <span class="hil-field-label" lang="de">${vm.t("hil.model")}</span>
        <select
          class="hil-model"
          data-field="model"
          @change=${(e: Event) => vm.modelChoice.set((e.target as HTMLSelectElement).value)}
        >
          ${unlisted
            ? html`<option value=${review.model} ?selected=${selected === review.model}>
                ${vm.t("hil.model.unlisted", { model: review.model })}
              </option>`
            : nothing}
          ${models.map(
            (m) => html`<option value=${m.id} ?selected=${m.id === selected}>${m.label}</option>`,
          )}
          <option value=${MODEL_OTHER} ?selected=${isOther}>${vm.t("hil.model.other")}</option>
        </select>
        ${isOther
          ? html`<input
                class="hil-model-other"
                data-field="modelOther"
                type="text"
                placeholder=${vm.t("hil.model.otherPlaceholder")}
                spellcheck="false"
              />
              <span class="hil-note" lang="de">${vm.t("hil.model.otherNote")}</span>`
          : unlisted
            ? html`<span class="hil-note" lang="de">${vm.t("hil.model.unlistedNote")}</span>`
            : nothing}
      </label>
    `
  }

  private numberField(vm: LlmReviewVm, field: string, labelKey: "hil.temperature" | "hil.maxTokens", value: number | undefined, attrs: { min: number; max: number; step: number }): TemplateResult {
    return html`
      <label class="hil-field hil-field--num">
        <span class="hil-field-label" lang="de">${vm.t(labelKey)}</span>
        <input
          class="hil-num"
          data-field=${field}
          type="number"
          inputmode="decimal"
          min=${attrs.min}
          max=${attrs.max}
          step=${attrs.step}
          placeholder=${vm.t("hil.param.unset")}
          .value=${value != null ? String(value) : ""}
        />
      </label>
    `
  }

  private sheet(vm: LlmReviewVm, review: PendingReviewView): TemplateResult {
    const left = vm.remaining(review)
    const busy = vm.busy.value
    const schema = review.responseSchema !== undefined ? JSON.stringify(review.responseSchema, null, 2) : null
    return html`
      <!-- The scrim is what makes the sheet read as something that CAME FORWARD rather than a slab
           dropped on the page. It swallows clicks on purpose and does not dismiss: the action really
           is blocked, and neither "approve" nor "reject" is a safe thing to infer from a stray click. -->
      <div class="hil-scrim" aria-hidden="true"></div>
      <section class="hil" role="dialog" aria-modal="true" aria-labelledby="hil-title" data-review-id=${review.id}>
        <header class="hil-head">
          <h2 id="hil-title" class="hil-title" lang="de">${vm.t("hil.title")}</h2>
          <p class="hil-origin" lang="de">
            ${vm.t("hil.origin", {
              seam: review.seam ?? vm.t("hil.seam.unknown"),
              action: review.action ?? vm.t("hil.action.unknown"),
            })}
          </p>
          <p class=${classMap({ "hil-deadline": true, "is-urgent": left <= 30 })} lang="de" role="timer">
            ${vm.t("hil.deadline", { seconds: String(left) })}
          </p>
        </header>

        <p class="hil-binding" lang="de">${vm.t("hil.binding", { engine: review.engine, model: review.model })}</p>

        <div class="hil-body">
          ${this.messageField(vm, "system", review.system)}
          ${this.messageField(vm, "user", review.user)}

          ${schema
            ? html`<details class="hil-schema">
                <summary class="hil-schema-summary" lang="de">${vm.t("hil.schema")}</summary>
                <pre class="hil-pre">${schema}</pre>
              </details>`
            : nothing}
          ${vm.error.value
            ? html`<p class="hil-error" role="alert" lang="de">${vm.t("hil.error", { message: vm.error.value })}</p>`
            : nothing}
        </div>

        <!-- The call's PARAMETERS live outside the scrolling body, beside the buttons: they are part
             of the decision, not part of the reading material. Inside the body they were the first
             thing a scroll cut in half — a label with its input below the fold reads as broken. -->
        <div class="hil-controls">
          ${this.modelField(vm, review)}
          ${this.numberField(vm, "temperature", "hil.temperature", review.params.temperature, { min: 0, max: 2, step: 0.1 })}
          ${this.numberField(vm, "maxTokens", "hil.maxTokens", review.params.maxTokens, { min: 1, max: 200000, step: 1 })}
        </div>

        <footer class="hil-foot">
          <span class="hil-queue" lang="de">
            ${vm.pending.value.length > 1 ? vm.t("hil.queue", { n: String(vm.pending.value.length - 1) }) : nothing}
          </span>
          <button type="button" class="hil-reject" data-act="reject-llm-call" ?disabled=${busy} lang="de">
            ${vm.t("hil.reject")}
          </button>
          <button type="button" class="hil-approve" data-act="approve-llm-call" ?disabled=${busy} lang="de">
            ${busy ? vm.t("hil.sending") : vm.t("hil.approve")}
          </button>
        </footer>
      </section>
    `
  }

  protected render(vm: LlmReviewVm): TemplateResult {
    const review = vm.current()
    // Nothing pending ⇒ no chrome at all. The element is invisible until it has a job to do.
    if (!review) return html``
    return this.sheet(vm, review)
  }

  /**
   * Read the edits out of the DOM. Only fields the operator actually CHANGED are sent — the server
   * treats an omitted field as "keep what the seam built", so an untouched pane approves the call
   * exactly as the seam wrote it and the run is honestly recorded as un-edited.
   */
  private readEdits(root: Element, review: PendingReviewView): Record<string, unknown> {
    const out: Record<string, unknown> = {}
    const text = (field: string): string | undefined =>
      root.querySelector<HTMLTextAreaElement>(`textarea[data-field="${field}"]`)?.value
    const system = text("system")
    const user = text("user")
    if (system !== undefined && system !== review.system) out.system = system
    if (user !== undefined && user !== review.user) out.user = user

    // The field is a <select> when the allowlist reached us, a text input when it did not; the
    // "other" option hands back to a text input beside it. All three land on one string.
    const picked = root.querySelector<HTMLSelectElement | HTMLInputElement>('[data-field="model"]')?.value.trim()
    const model =
      picked === MODEL_OTHER
        ? root.querySelector<HTMLInputElement>('input[data-field="modelOther"]')?.value.trim()
        : picked
    if (model && model !== review.model) out.model = model

    const numOf = (field: string, int = false): number | undefined => {
      const raw = root.querySelector<HTMLInputElement>(`input[data-field="${field}"]`)?.value?.trim()
      if (!raw) return undefined
      const n = int ? Number.parseInt(raw, 10) : Number.parseFloat(raw)
      return Number.isFinite(n) ? n : undefined
    }
    const temperature = numOf("temperature")
    const maxTokens = numOf("maxTokens", true)
    if (temperature !== undefined && temperature !== review.params.temperature) out.temperature = temperature
    if (maxTokens !== undefined && maxTokens !== review.params.maxTokens) out.maxTokens = maxTokens
    return out
  }

  protected override actions(vm: LlmReviewVm): ActionMap {
    const currentRoot = (event: Event): { root: Element; review: PendingReviewView } | null => {
      const review = vm.current()
      const root = (event.target as HTMLElement | null)?.closest(".hil")
      return review && root ? { root, review } : null
    }
    return {
      "approve-llm-call": (_params, event) => {
        const ctx = currentRoot(event)
        if (!ctx) return
        void vm.resolve(ctx.review.id, { action: "approve", ...this.readEdits(ctx.root, ctx.review) })
      },
      "reject-llm-call": (_params, event) => {
        const ctx = currentRoot(event)
        if (!ctx) return
        void vm.resolve(ctx.review.id, { action: "reject" })
      },
    }
  }
}
